const React = require('react');
const T = require('prop-types');

const { default: Chip } = require('@mui/material/Chip');
const { default: Styled } = require('styled-components');
const { transient$Props } = require('utils/styles');

const { default: AddCircleRoundedIcon } = require('@mui/icons-material/AddCircleRounded');
const { default: CancelIcon } = require('@mui/icons-material/Cancel');

const internals = {};

function getInterestSvgData(interest, category) {
    const svg = (interest && interest.svg) ? interest.svg : ((category && category.svg) ? category.svg : null);

    if (!svg) {
        return '';
    }

    const buff = Buffer.from(svg);
    return `data:image/svg+xml;base64,${buff.toString('base64')}`;
}

function InterestChip(props) {
    const {
        interest,
        category,
        className,
        handleClick,
        isCommonInterest,
        isMe,
        isPassion,
        onDelete,
        indicatorZIndex = 1,
        deleteIconVariant = 'add',
        ariaLabel,
        ...rest
    } = props;

    const imageData = getInterestSvgData(interest, category);
    const resolvedOnDelete = typeof onDelete === 'undefined' && deleteIconVariant === 'add' && !isCommonInterest
        ? handleClick
        : onDelete;
    const deleteIcon = deleteIconVariant === 'cancel'
        ? <div style={{ display: 'flex' }} data-focus-outline={`radius:16,zIndex:${indicatorZIndex}`}><CancelIcon /></div>
        : <AddCircleRoundedIcon />;

    const defaultAriaLabel = isMe
        ? `Interest ${interest.name}.`
        : `Interest ${interest.name}. ${isCommonInterest ? 'You have this interest in common.' : 'Press enter to add to your own list of interests.'}`;

    return (
        <internals.StyledInterestChip
            avatar={imageData && <img alt={`${interest.name} icon`} src={imageData} />}
            className={className}
            data-focus-outline={`radius:16,inside,zIndex:${indicatorZIndex}`}
            deleteIcon={deleteIcon}
            isCommonInterest={isCommonInterest}
            label={interest.name}
            onClick={handleClick}
            onDelete={resolvedOnDelete}
            tabIndex={0}
            $isPassion={isPassion}
            aria-label={ariaLabel || `${isPassion ? 'Passion ' : ''}${defaultAriaLabel}`}
            {...rest}
        />
    );
}

InterestChip.propTypes = {
    interest: T.object.isRequired,
    category: T.object,
    className: T.string,
    handleClick: T.func,
    isCommonInterest: T.bool,
    isMe: T.bool,
    isPassion: T.bool,
    onDelete: T.func,
    indicatorZIndex: T.number,
    deleteIconVariant: T.oneOf(['add', 'cancel']),
    ariaLabel: T.string
};

internals.StyledInterestChip = Styled(Chip, transient$Props)`
    && {
        height: 40px;
        padding: 5px;
        border-radius: 20px;
        border: ${({ isCommonInterest }) => isCommonInterest ? '1px solid transparent' : '1px solid transparent'};
        background-color: ${({ $isPassion, theme }) => $isPassion ? `${theme.palette.primary.main}1f` : theme.palette.background.paper};
        background-image: none;
        box-shadow: ${({ isCommonInterest, $isPassion, theme }) => {
            if (isCommonInterest) {
                return `0 0 0 3px ${theme.palette.primary.main}, 0 0 0 1px transparent inset`;
            }

            if ($isPassion) {
                return `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 1px transparent inset`;
            }

            return '0 0 0 2px transparent, 0 0 0 1px rgba(0, 0, 0, 0.06) inset';
        }};
        color: ${({ theme }) => theme.palette.text.primary};
        font-family: ${({ $isPassion, theme }) => $isPassion && theme.typography.fonts.Mulish};
        font-weight: ${({ $isPassion }) => $isPassion ? '700' : '600'};
        transition: background-color 120ms ease, box-shadow 120ms ease;

        &:hover,
        &.Mui-focusVisible {
            background-color: ${({ theme }) => `${theme.palette.primary.main}14`};
            box-shadow: ${({ isCommonInterest, $isPassion, theme }) => {
                if (isCommonInterest) {
                    return `0 0 0 3px ${theme.palette.primary.main}, 0 0 0 1px transparent inset`;
                }

                if ($isPassion) {
                    return `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 1px transparent inset`;
                }

                return `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 1px transparent inset`;
            }};
        }

        &:focus {
            outline: none;
        }

        & .MuiChip-label {
            padding-left: 10px;
            padding-right: 10px;
        }

        & .MuiChip-avatar {
            box-sizing: border-box !important;
            width: 36px !important;
            height: 36px !important;
            margin-left: 2px;
            padding: 1px !important;
            border-radius: 0 !important;
            background: transparent !important;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        & .MuiChip-avatar img {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain !important;
        }

        & .MuiChip-deleteIcon {
            color: ${({ theme }) => theme.palette.text.secondary};

            &:hover {
                color: ${({ theme }) => theme.palette.primary.main};
            }
        }
    }
`;

module.exports = InterestChip;
